import { useState } from 'react';

import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

import { useAppSelector } from '@/store/hooks';
import { useGetOrgByIdQuery } from '@/store/slices/api/organizationApiSlice';
import { useUpdatePatientMutation } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';
import { usePatchTransmissionReportMutation } from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';
import { capitalize } from '@/utils/constants';

import { RegisteredNpi } from '@/types/npi';
import LabeledField from '@/ui/LabeledField';

import styles from '../CareTeamDetails.module.scss';
import ReferringProviderSelectionModal from '@/pages/physician/PatientOverviewPage/ReferringProviderSelectionModal';
import { getReferringProviderName } from '@/pages/physician/PatientOverviewPage/helpers';

const ReferringProviderSelect = ({
  clinicId,
  patientId,
  referringPhysician,
  referringProvider,
  isReportEditMode,
  reportId,
  labelVariant = 'allCaps',
  editOnHover = false
}: {
  clinicId: string;
  patientId: string;
  referringPhysician: string | null;
  referringProvider: RegisteredNpi | null;
  isReportEditMode: boolean;
  reportId?: number;
  labelVariant?: 'allCaps' | 'regular' | 'sentence';
  editOnHover?: boolean;
}) => {
  const [updatePatient, { isLoading: isUpdatingPatient }] = useUpdatePatientMutation();
  const [updateReport, { isLoading: isUpdatingReport }] = usePatchTransmissionReportMutation();

  const [opened, { toggle: toggleReferringProviderModal }] = useDisclosure(false);

  const { data: clinicData, isLoading: isLoadingClinic } = useGetOrgByIdQuery(clinicId, {
    skip: !clinicId
  });
  const isEHRIntegrated =
    clinicData?.organization?.parent?.ehrIntegration ??
    clinicData?.organization?.ehrIntegration ??
    false;

  const [providerName, setProviderName] = useState<string>(() =>
    getReferringProviderName({
      referringProvider: referringProvider,
      isEHRIntegrated,
      referringPhysician
    })
  );

  const { isActiveReportFinalSigned: isReportFinalSigned } = useAppSelector(
    (state) => state.transmissionReportForm
  );

  const handleReferringProviderChange = async (provider: RegisteredNpi | null) => {
    try {
      if (reportId && isReportFinalSigned) {
        await updateReport({
          id: reportId,
          referringProvider: provider ? provider.NPI : '-'
        }).unwrap();
      } else {
        await updatePatient({
          patient: {
            id: patientId,
            referringProvider: provider
              ? ({ NPI: provider.NPI } as RegisteredNpi)
              : ({ NPI: null } as unknown as RegisteredNpi)
          },
          activeReportId: reportId
        }).unwrap();
      }

      notifications.show({
        title: 'Success',
        message: 'Referring provider updated successfully',
        color: 'green'
      });
    } catch (error) {
      console.error('Error updating referring provider:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update referring provider. Please try again.',
        color: 'red'
      });
    }
  };

  const handleProviderSelect = (newReferringProvider: RegisteredNpi) => {
    handleReferringProviderChange(newReferringProvider);
    setProviderName(
      getReferringProviderName({ referringProvider: newReferringProvider, isEHRIntegrated })
    );
  };

  return (
    <>
      <LabeledField
        label="Referring Provider"
        labelVariant={labelVariant}
        editOnHover={editOnHover}
        showEditIcon={isReportEditMode}
        onEdit={toggleReferringProviderModal}
        isLoading={isUpdatingPatient || isUpdatingReport || isLoadingClinic}
        valueClassName={styles.referringProviderSelectValue}
        value={
          <div className={styles.providerNameWrapper}>
            <span>
              {providerName && providerName !== '-' ? capitalize(providerName) : 'Not assigned'}
            </span>
            {providerName && providerName !== '-' && isReportEditMode && (
              <span
                className={styles.clearIcon}
                onClick={() => {
                  setProviderName('');
                  handleReferringProviderChange(null);
                }}>
                ✕
              </span>
            )}
          </div>
        }
      />
      <ReferringProviderSelectionModal
        opened={opened}
        onClose={toggleReferringProviderModal}
        onProviderSelect={handleProviderSelect}
      />
    </>
  );
};

export default ReferringProviderSelect;
